import * as React from 'react';
import {Query} from 'react-apollo';
import {Helmet} from 'react-helmet';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter, Location, Match} from 'react-router-dom';

// @ts-expect-error
import memoize from 'memoize-one';

import {translateConnectionObjectToArray} from 'nutshell-core';

import {convertToArray} from 'nutshell-core/filter';
import {Routing} from 'nutshell-core/routing';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {headTitle} from 'shells/document/head-title';
import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';
import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';
import {LinkedEntity} from 'shells/linked-entity/linked-entity';

import type {
    GetScheduledEmails as GetScheduledEmailsQuery,
    GetScheduledEmailsVariables as GetScheduledEmailsQueryVariables,
    QueuedEmail as QueuedEmailFragment,
} from 'nutshell-graphql-types';

import {GetEntity} from '../../../core/get-entity';

import {nutApolloClient} from '../../../../apollo-client';
import {appHistory} from '../../../../history';

import {AutomatedEmailPausedBanner} from '../../automated-email-paused-banner';
import {EmailSetupCta} from '../../email-setup-cta';
import {hasEmailSequenceCapability} from '../../has-email-sequence-capability';
import {ProPlanInfo} from '../../templates/pro-plan-info';

import {EmailList} from '../email-list';
import {FilterBar} from '../filter-bar';
import GET_SCHEDULED_EMAILS from '../graphql/queries/get-scheduled-emails.graphql';
import {isSameDay, ERROR_STATE} from '../utils';

import {QueuedEmailRowItem} from './queued-email-row-item';

import styles from '../email-list.module.css';

const LIMIT = 50;

type OwnProps = {
    onOpenVideoModal: () => any;
};

type WithQueuedEmails = {
    isLoading: boolean;
    emails: QueuedEmailFragment[];
    hasNextPage: boolean;
    loadEmails: (arg1: Filter) => Promise<any>;
    onRefetch: () => any;
    queuedEmailErrorMessage: string | null | undefined;
};

type Props = OwnProps & WithQueuedEmails & ContextRouter;

type State = {
    isUpdating: boolean;
};

type DateHeader = {
    __typename: 'DateHeader';
    timestamp: number;
};

type Filter = {
    entityId: string | null | undefined;
    messageId: string | null | undefined;
};

function addHeaders(emails: QueuedEmailFragment[]) {
    return emails.reduce<Array<any>>((acc, email, idx) => {
        // @ts-expect-error
        if (!emails[idx - 1] || !isSameDay(email.deliveryTime, emails[idx - 1].deliveryTime)) {
            acc.push({__typename: 'DateHeader', timestamp: email.deliveryTime});
        }

        acc.push(email);

        return acc;
    }, []);
}

export class ScheduledMailComponent extends React.Component<Props, State> {
    addHeadersToEmails: (arg1: QueuedEmailFragment[]) => QueuedEmailFragment | DateHeader[];

    constructor() {
        // @ts-expect-error
        super();
        // @ts-expect-error
        this.addHeadersToEmails = memoize((emails) => addHeaders(emails));
        this.state = {
            isUpdating: false,
        };
    }

    componentDidMount() {
        window.addEventListener('refreshScheduledEmails', this.refreshEmails);
    }

    componentWillUnmount() {
        window.removeEventListener('refreshScheduledEmails', this.refreshEmails);
    }

    render() {
        const emails = this.addHeadersToEmails(this.props.emails);
        const isEmailConfigured = NutClientConfig.hasConfiguredEmail;

        const emptyStateUnconfigured = (
            <EmptyStateLarge
                icon={
                    <img
                        src='/include/images/email-marketing/email_scheduled_empty_state.png'
                        className={styles['large-icon']}
                    />
                }
                detailTextBullets={[
                    'Create email templates that include automatic, timed follow-up messages.',
                    'Want to go fast? Include variables like {firstName}, {lastName}, and {companyName} so your message composition is a breeze.',
                ]}
                cta={<EmailSetupCta />}
                title='Automatic follow-up emails. Like magic.'
                subtitle='Configure Nutshell to send follow-up emails automatically. They’ll show up here when you do.'
            />
        );
        const emptyStateConfigured = (
            <EmptyStateSmall
                icon={<img alt='' src='/include/images/email-drip/email-scheduled_2x.png' />}
                title='You don’t have any scheduled follow-ups'
                subtitle='Configure Nutshell to send follow-up emails automatically. They’ll show up here when you do.'
                cta={
                    <Button variant='primary' size='big' onClick={this.props.onOpenVideoModal}>
                        Learn more
                    </Button>
                }
            />
        );
        const proState = (
            <div className={styles['email-list']}>
                {this.renderFilterBar()}
                <EmailList
                    isRequesting={this.props.isLoading}
                    isUpdating={this.state.isUpdating}
                    onFetchMoreRows={this.fetchMoreRows}
                    hasNextPage={this.props.hasNextPage}
                    // @ts-expect-error
                    collection={emails}
                    specialStates={[
                        {
                            shouldRender: () => Boolean(this.props.queuedEmailErrorMessage),
                            component: ERROR_STATE,
                        },
                        {
                            shouldRender: () => !isEmailConfigured,
                            component: emptyStateUnconfigured,
                        },
                        {
                            // @ts-expect-error
                            shouldRender: () => emails.length === 0 && !this.props.isLoading,
                            component: emptyStateConfigured,
                        },
                    ]}
                    renderRow={(emailItem: QueuedEmailFragment, isFirstOfDate: boolean) => {
                        const filters = getFiltersFromProps(this.props);

                        // If we're filtering by an entity, try to sort them to the front,
                        // so we show their name as the recipient if there are more than one.
                        const entityId = getEntityIdFromFilterLocationSearchString(
                            this.props.location.search
                        );
                        if (entityId) {
                            emailItem.to = emailItem.to.sort((a, b) => {
                                if (a.avatarEntity && a.avatarEntity.id === entityId) return -1;
                                if (b.avatarEntity && b.avatarEntity.id === entityId) return 1;

                                return 0;
                            });
                        }

                        return (
                            <QueuedEmailRowItem
                                key={emailItem.id}
                                isFirstOfDate={isFirstOfDate}
                                queuedEmail={emailItem}
                                onEmailUpdate={this.beginUpdatingList}
                                // @ts-expect-error
                                onEmailRetried={updateCacheFromRetry(filters, this.endUpdatingList)}
                                // @ts-expect-error
                                onEmailSendNow={updateCacheFromSendNow(
                                    filters,
                                    this.endUpdatingList
                                )}
                                // @ts-expect-error
                                onEmailDelayed={updateCacheFromDelay(filters, this.endUpdatingList)}
                                // @ts-expect-error
                                onSequenceDeleted={updateCacheFromDelete(
                                    filters,
                                    this.endUpdatingList
                                )}
                                onEmailEdit={() => {
                                    appHistory.push({
                                        hash: `/edit-scheduled-email/${emailItem.id}`,
                                    });
                                }}
                                navToSequenceDetails={
                                    // Only allow pushing to sequence detail page if we're not already on it
                                    !this.getMessageToFilterBy()
                                        ? () => {
                                              this.props.history.push(emailItem.htmlUrl);
                                          }
                                        : undefined
                                }
                            />
                        );
                    }}
                />
            </div>
        );

        return (
            <React.Fragment>
                <Helmet>
                    <title>{headTitle('Scheduled emails')}</title>
                </Helmet>
                <AutomatedEmailPausedBanner />
                {hasEmailSequenceCapability() ? proState : <ProPlanInfo />}
            </React.Fragment>
        );
    }

    beginUpdatingList = () => {
        this.setState({
            isUpdating: true,
        });
    };

    endUpdatingList = () => {
        this.setState({
            isUpdating: false,
        });
    };

    fetchMoreRows = (): Promise<any> => {
        return this.props.loadEmails(getFiltersFromProps(this.props));
    };

    refreshEmails = () => {
        this.props.onRefetch();
    };

    renderFilterBar = () => {
        const filters = getFiltersFromProps(this.props);
        const entityId = filters.entityId;
        const messageId = filters.messageId;
        const message = this.getMessageToFilterBy();

        if (!this.isFilterApplied(entityId, messageId)) {
            return null;
        }

        return (
            <FilterBar backButtonText='All scheduled emails'>
                <div>
                    {entityId ? (
                        <GetEntity entityId={entityId}>
                            {({entity}) => {
                                return (
                                    <div className={styles['filter-text-container']}>
                                        Showing scheduled emails to
                                        <LinkedEntity
                                            stopPropagation={true}
                                            shouldClientRoute={true}
                                            htmlUrl={entity.htmlUrl || undefined}
                                            type={entity.type}
                                            name={entity.name}
                                        />
                                    </div>
                                );
                            }}
                        </GetEntity>
                    ) : undefined}
                    {message ? (
                        <GetEntity
                            entityId={
                                // Get the entity the email sequence is to. If
                                // there is no entity, return an empty string.
                                // @ts-expect-error
                                message.to.length && message.to[0].avatarEntity
                                    ? // @ts-expect-error
                                      message.to[0].avatarEntity.id
                                    : ''
                            }
                        >
                            {({entity}) => {
                                return (
                                    <div className={styles['filter-text-container']}>
                                        Showing emails in
                                        {message.emailSequence &&
                                        message.emailSequence.emailSequenceTemplate ? (
                                            <strong>
                                                {message.emailSequence.emailSequenceTemplate.name}
                                            </strong>
                                        ) : (
                                            'sequence'
                                        )}
                                        {entity ? (
                                            <div className='flex'>
                                                &nbsp;to&nbsp;
                                                <strong>
                                                    <LinkedEntity
                                                        stopPropagation={true}
                                                        htmlUrl={entity.htmlUrl || undefined}
                                                        type={entity.type}
                                                        name={entity.name}
                                                    />
                                                </strong>
                                            </div>
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                );
                            }}
                        </GetEntity>
                    ) : undefined}
                </div>
            </FilterBar>
        );
    };

    isFilterApplied = (entityId?: string | null, messageId?: string | null) => {
        // Return true if entityId exists, or if messageId exists and has length
        return Boolean(entityId) || Boolean(messageId);
    };

    getMessageToFilterBy = (): QueuedEmailFragment | null | undefined => {
        const messageId = this.props.match.params.scheduledMessageId;

        if (!messageId || messageId.length === 0) {
            return undefined;
        }

        // @ts-expect-error
        return this.props.emails.find((email) => {
            return email.id === messageId;
        });
    };
}

function getFiltersFromProps(props: {location: Location; match: Match}): Filter {
    return {
        entityId: getEntityIdFromFilterLocationSearchString(props.location.search),
        messageId: props.match.params.scheduledMessageId,
    };
}

function getEntityIdFromFilterLocationSearchString(
    search?: string | null
): string | null | undefined {
    const activeFilters = getFiltersFromLocationSearchString(search);
    const entityFilter = activeFilters.find((filter) => {
        return filter.entityId;
    });

    return entityFilter ? entityFilter.entityId : undefined;
}

function getFiltersFromLocationSearchString(search?: string | null): any[] {
    if (!search) {
        return [];
    }

    const urlParamString = search.slice(1); // Remove "?"
    const urlParams: any = Routing.deparam(urlParamString);
    const activeFilters = convertToArray(urlParams.filter);

    return activeFilters;
}

// @ts-expect-error
function updateCachedQueuedEmailsForSequence(proxy, emailSequence, filters) {
    const updates = emailSequence.emailsQueued;

    const cacheResult: GetScheduledEmailsQuery | null | undefined = proxy.readQuery({
        query: GET_SCHEDULED_EMAILS,
        variables: {
            limit: LIMIT,
            filters: filters,
        },
    });

    if (!cacheResult) return;

    // Update the delivery time of the cached emails
    cacheResult.queuedEmails.edges = cacheResult.queuedEmails.edges.map((edge) => {
        // @ts-expect-error
        const update = updates.find((email) => email.id === edge.node.id);

        return update
            ? {
                  ...edge,
                  node: {
                      ...edge.node,
                      status: update.status ? update.status : edge.node.status,
                      sendProblems: update.sendProblems
                          ? update.sendProblems
                          : edge.node.sendProblems,
                      deliveryTime: update.deliveryTime,
                  },
              }
            : edge;
    });

    // Sort them by the new delivery dates
    cacheResult.queuedEmails.edges.sort((a, b) => a.node.deliveryTime - b.node.deliveryTime);

    // Write our data back to the data.
    proxy.writeQuery({
        query: GET_SCHEDULED_EMAILS,
        variables: {
            limit: LIMIT,
            filters: filters,
        },
        data: cacheResult,
    });
}

// @ts-expect-error
function updateCacheFromRetry(filters, endUpdate) {
    // @ts-expect-error
    return function (proxy, {data}) {
        if (data) {
            updateCachedQueuedEmailsForSequence(
                proxy,
                data.emailQueuedRetry.emailSequence,
                filters
            );
        }

        endUpdate();
    };
}

// @ts-expect-error
function updateCacheFromSendNow(filters, endUpdate) {
    // @ts-expect-error
    return function (proxy, {data}) {
        if (data) {
            updateCachedQueuedEmailsForSequence(
                proxy,
                data.emailQueuedSendNow.emailSequence,
                filters
            );
        }
        endUpdate();
    };
}

// @ts-expect-error
function updateCacheFromDelay(filters, endUpdate) {
    // @ts-expect-error
    return function (proxy, {data}) {
        if (data) {
            updateCachedQueuedEmailsForSequence(
                proxy,
                data.delayQueuedEmail.emailSequence,
                filters
            );
        }
        endUpdate();
    };
}

// @ts-expect-error
function updateCacheFromDelete(filters, endUpdate) {
    // @ts-expect-error
    return function (proxy, {data}) {
        if (data) {
            const deletedSequenceId = data.emailSequenceDelete.emailSequence.id;

            // Read the data from our cache for this query.
            const cacheResult: GetScheduledEmailsQuery | null | undefined = proxy.readQuery({
                query: GET_SCHEDULED_EMAILS,
                variables: {
                    limit: LIMIT,
                    filters: filters,
                },
            });

            if (!cacheResult) return;

            // Remove deleted items from cache
            cacheResult.queuedEmails.edges = cacheResult.queuedEmails.edges.filter(
                (edge) =>
                    edge.node &&
                    edge.node.emailSequence &&
                    edge.node.emailSequence.id !== deletedSequenceId
            );

            // Write our data back to the cache.
            proxy.writeQuery({
                query: GET_SCHEDULED_EMAILS,
                variables: {
                    limit: LIMIT,
                    filters: filters,
                },
                data: cacheResult,
            });
        }

        endUpdate();
    };
}

/*
 * react-apollo gives us stale data when the variables change if it hasn't updated the cache,
 * so we need to check if we have good data before showing it.
 *
 * ref: https://github.com/apollographql/react-apollo/issues/2202
 */
function checkCacheExists(entityId?: string | null, messageId?: string | null) {
    try {
        nutApolloClient.readQuery({
            query: GET_SCHEDULED_EMAILS,
            variables: {
                limit: LIMIT,
                filters: {
                    entityId,
                    messageId,
                },
            },
        });

        return true;
    } catch (_e: any) {
        return false;
    }
}

class ScheduledEmailQuery extends Query<
    GetScheduledEmailsQuery,
    GetScheduledEmailsQueryVariables
> {}

const ScheduledMailGraphQL = (props: OwnProps & ContextRouter) => (
    <ScheduledEmailQuery
        query={GET_SCHEDULED_EMAILS}
        // isLoading only changes on `loadEmails` if this option is true
        notifyOnNetworkStatusChange={true}
        variables={{
            limit: LIMIT,
            filters: getFiltersFromProps(props),
        }}
    >
        {({data, loading, refetch, error, fetchMore}) => {
            const filters = getFiltersFromProps(props);
            const entityId = filters.entityId;
            const messageId = filters.messageId;
            const isGoodData = checkCacheExists(entityId, messageId);
            const queuedEmails = data ? data.queuedEmails : null;

            return (
                <ScheduledMailComponent
                    history={props.history}
                    location={props.location}
                    match={props.match}
                    onOpenVideoModal={props.onOpenVideoModal}
                    isLoading={loading}
                    onRefetch={refetch}
                    // @ts-expect-error
                    queuedEmailErrorMessage={safelyGetGraphQLErrorMessage(error)}
                    // We defualt to true here, because on the first load, we need to
                    // display a loading icon, which is only displayed if we have a next
                    // page
                    hasNextPage={
                        isGoodData && queuedEmails ? queuedEmails.pageInfo.hasNextPage : true
                    }
                    emails={
                        isGoodData && queuedEmails
                            ? translateConnectionObjectToArray(queuedEmails)
                            : []
                    }
                    loadEmails={(filter: Filter) =>
                        fetchMore({
                            query: GET_SCHEDULED_EMAILS,
                            variables: {
                                limit: LIMIT,
                                filters: filter,
                                cursor: queuedEmails ? queuedEmails.pageInfo.endCursor : null,
                            },
                            updateQuery: (previousResult, {fetchMoreResult}) => {
                                if (!fetchMoreResult) return previousResult;
                                const previousEdges = previousResult.queuedEmails.edges;
                                const newEdges = fetchMoreResult.queuedEmails.edges;
                                const newTotalCount = fetchMoreResult.queuedEmails.totalCount;
                                const newPageInfo = fetchMoreResult.queuedEmails.pageInfo;

                                return {
                                    // Put the new emails at the end of the list and update `pageInfo`
                                    // so we have the new `endCursor` and `hasNextPage` values
                                    queuedEmails: {
                                        __typename: 'EmailsQueuedConnection',
                                        totalCount: newTotalCount,
                                        pageInfo: newPageInfo,
                                        edges: [...previousEdges, ...newEdges],
                                    },
                                };
                            },
                        })
                    }
                />
            );
        }}
    </ScheduledEmailQuery>
);

// @ts-expect-error
export const ScheduledMail = withRouter<OwnProps>(ScheduledMailGraphQL);
